import { computed, inject, Injectable, signal } from "@angular/core";
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { Store } from "@ngrx/store";
import { first } from "rxjs/operators";
import { environment } from "../../../configuration";
import { AppUser, AppUserSelectors } from "../../modules/shared/app-user";
import { AuthActions } from "../../auth/store/actions";
import { MessageReference, MessageService } from "../message/message.service";
import { NotificationMessageSeverity } from "../message/notification-message.component";

@Injectable({
    providedIn: "root",
})
export class SessionTimeoutService {
    private readonly idle = inject(Idle);
    private readonly store = inject<Store<{ appUser: AppUser }>>(Store);
    private readonly messageService = inject(MessageService);
    private started = false;
    private timeoutWarningNotification: MessageReference | undefined;
    public readonly timeoutWarningCountdown = signal(0);

    public start(): void {
        if (this.started) {
            return;
        }
        this.started = true;

        this.idle.setIdle(environment.inactivityTime);
        this.idle.setTimeout(environment.timeoutWarning);
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        this.idle.onIdleEnd.subscribe(() => this.dismissTimeoutWarning());
        this.idle.onTimeout.subscribe(() => this.handleTimeout());
        this.idle.onTimeoutWarning.subscribe((countdown) => this.handleTimeoutWarning(countdown));
        this.idle.watch();
    }

    private handleTimeout(): void {
        this.dismissTimeoutWarning();
        this.store
            .select(AppUserSelectors.user)
            .pipe(first())
            .subscribe((appUser) => {
                if (appUser) {
                    this.store.dispatch(AuthActions.logout());
                }
            });
    }

    private handleTimeoutWarning(countdown: number): void {
        this.store
            .select(AppUserSelectors.user)
            .pipe(first())
            .subscribe((appUser) => {
                if (!appUser) {
                    return;
                }

                this.timeoutWarningCountdown.set(countdown);
                if (!this.timeoutWarningNotification || this.timeoutWarningNotification.isClosed()) {
                    this.timeoutWarningNotification = this.messageService.show(
                        {
                            message: computed(
                                () => `You will be logged out in ${this.timeoutWarningCountdown()} seconds.`,
                            ),
                            severity: NotificationMessageSeverity.Info,
                        },
                        { dismissal: "manual", priority: true },
                    );
                }
            });
    }

    private dismissTimeoutWarning(): void {
        this.timeoutWarningNotification?.close();
        this.timeoutWarningNotification = undefined;
    }
}
